import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Avatar,
  InputAdornment,
} from '@mui/material';

import config from "../twh/config";
import { getFirstErrorMessage } from "../twh/functions";

// SVG icon
import iconAngleDown from '../assets/icons/angle-down.svg';

export default function MenuPage() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Subcategories dialog
  const [openSubDialog, setOpenSubDialog] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedCategoryName, setSelectedCategoryName] = useState('');
  const [subcategories, setSubcategories] = useState([]);
  const [subLoading, setSubLoading] = useState(false);
  const [subError, setSubError] = useState(null);

  // Products dialog
  const [openProductsDialog, setOpenProductsDialog] = useState(false);
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState(null);
  const [selectedSubcategoryName, setSelectedSubcategoryName] = useState('');
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState(null);

  // Category Add/Edit
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentCatId, setCurrentCatId] = useState('');
  const [catName, setCatName] = useState('');
  const [catFormError, setCatFormError] = useState('');
  const [catFormSuccess, setCatFormSuccess] = useState('');
  const [catSubmitting, setCatSubmitting] = useState(false);

  // Subcategory Add/Edit
  const [openSubcategoryDialog, setOpenSubcategoryDialog] = useState(false);
  const [isSubEditMode, setIsSubEditMode] = useState(false);
  const [currentSubId, setCurrentSubId] = useState('');
  const [subName, setSubName] = useState('');
  const [subFormError, setSubFormError] = useState('');
  const [subFormSuccess, setSubFormSuccess] = useState('');
  const [subSubmitting, setSubSubmitting] = useState(false);

  // Product Add/Edit
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [isProductEditMode, setIsProductEditMode] = useState(false);
  const [currentProdId, setCurrentProdId] = useState('');
  const [prodName, setProdName] = useState('');
  const [prodPrice, setProdPrice] = useState('');
  const [prodDescription, setProdDescription] = useState('');
  const [prodImageFile, setProdImageFile] = useState(null);
  const [prodImagePreview, setProdImagePreview] = useState('');
  const [prodFormError, setProdFormError] = useState('');
  const [prodFormSuccess, setProdFormSuccess] = useState('');
  const [prodSubmitting, setProdSubmitting] = useState(false);

  // Action menus
  const [subAnchorEl, setSubAnchorEl] = useState(null);
  const [selectedSubId, setSelectedSubId] = useState(null);

  const [prodAnchorEl, setProdAnchorEl] = useState(null);
  const [selectedProdId, setSelectedProdId] = useState(null);

  // Delete confirmation
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteType, setDeleteType] = useState(null);     // 'category' | 'subcategory' | 'product'
  const [deleteId, setDeleteId] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // ── Fetch Categories ──
  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${config.reactApi_baseUrl}/react-api/menu/categories.php`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.status === true) {
        setCategories(data.data || []);
      } else {
        throw new Error(data.message || data.errorMsg || 'API error');
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // ── Fetch Subcategories ──
  const fetchSubcategories = async () => {
    if (!selectedCategoryId) return;
    setSubLoading(true);
    setSubError(null);
    try {
      const res = await fetch(
        `${config.reactApi_baseUrl}/react-api/menu/subcategories.php?cat_id=${selectedCategoryId}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.status === true) {
        setSubcategories(data.data || []);
      } else {
        throw new Error(data.message || 'Failed');
      }
    } catch (err) {
      setSubError(err.message || 'Failed to load subcategories');
    } finally {
      setSubLoading(false);
    }
  };

  useEffect(() => {
    if (openSubDialog && selectedCategoryId) {
      fetchSubcategories();
    }
  }, [openSubDialog, selectedCategoryId]);

  // ── Fetch Products ──
  const fetchProducts = async () => {
    if (!selectedCategoryId || !selectedSubcategoryId) return;
    setProductsLoading(true);
    setProductsError(null);
    try {
      const res = await fetch(
        `${config.reactApi_baseUrl}/react-api/menu/products.php?cat_id=${selectedCategoryId}&subcat_id=${selectedSubcategoryId}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.status === true) {
        setProducts(data.data || []);
      } else {
        throw new Error(data.message || 'Failed');
      }
    } catch (err) {
      setProductsError(err.message || 'Failed to load products');
    } finally {
      setProductsLoading(false);
    }
  };

  useEffect(() => {
    if (openProductsDialog && selectedCategoryId && selectedSubcategoryId) {
      fetchProducts();
    }
  }, [openProductsDialog, selectedCategoryId, selectedSubcategoryId]);

  // ── Delete logic ────────────────────────────────────────────────
  const confirmDelete = (type, id) => {
    setDeleteType(type);
    setDeleteId(id);
    setDeleteError('');
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteType || !deleteId) return;

    setDeleteSubmitting(true);
    setDeleteError('');

    let url = '';
    let field = '';
    let refreshFunction = null;

    if (deleteType === 'category') {
      url = `${config.reactApi_baseUrl}/react-api/menu/delete-category.php`;
      field = 'cat_id';
      refreshFunction = fetchCategories;
    } else if (deleteType === 'subcategory') {
      url = `${config.reactApi_baseUrl}/react-api/menu/delete-subcategory.php`;
      field = 'subcat_id';
      refreshFunction = fetchSubcategories;
    } else if (deleteType === 'product') {
      url = `${config.reactApi_baseUrl}/react-api/menu/delete-product.php`;
      field = 'pro_id';
      refreshFunction = fetchProducts;
    }

    try {
      const formData = new FormData();
      formData.append('issetDeleteItem', '1');
      formData.append(field, deleteId);

      const res = await fetch(url, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const result = await res.json();

      if (result.status === true) {
        setOpenDeleteDialog(false);
        if (refreshFunction) refreshFunction();
      } else {
        setDeleteError(result.message || getFirstErrorMessage(result.errorMsg) || 'Delete failed');
      }
    } catch (err) {
      console.error('Delete error:', err);
      setDeleteError(err.message || 'Network or server error');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  // ── Category Handlers ──
  const openAddCategory = () => {
    setIsEditMode(false);
    setCurrentCatId('');
    setCatName('');
    setCatFormError('');
    setCatFormSuccess('');
    setOpenCategoryDialog(true);
  };

  const openEditCategory = (id) => {
    const cat = categories.find((c) => c.id === id);
    if (!cat) return;
    setIsEditMode(true);
    setCurrentCatId(id);
    setCatName(cat.name || '');
    setCatFormError('');
    setCatFormSuccess('');
    setOpenCategoryDialog(true);
    handleCloseMenu();
  };

  const handleSubmitCategory = async () => {
    if (!catName.trim()) {
      setCatFormError('Category name is required');
      return;
    }

    setCatSubmitting(true);
    setCatFormError('');
    setCatFormSuccess('');

    try {
      const formData = new FormData();
      formData.append('issetAddEditCat', '1');
      formData.append('name', catName.trim());
      if (isEditMode && currentCatId) {
        formData.append('cat_id', currentCatId);
      }

      const res = await fetch(`${config.reactApi_baseUrl}/react-api/menu/add-edit-category.php`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const result = await res.json();

      if (result.status === true) {
        setCatFormSuccess('Category saved successfully!');
        setTimeout(() => {
          fetchCategories();
          setOpenCategoryDialog(false);
        }, 1200);
      } else {
        setCatFormError(getFirstErrorMessage(result.errorMsg) || result.message || 'Failed to save category');
      }
    } catch (err) {
      setCatFormError(err.message || 'Network/server error');
      console.error(err);
    } finally {
      setCatSubmitting(false);
    }
  };

  // ── Subcategory Handlers ──
  const handleAddSubcategory = () => {
    setIsSubEditMode(false);
    setCurrentSubId('');
    setSubName('');
    setSubFormError('');
    setSubFormSuccess('');
    setOpenSubcategoryDialog(true);
  };

  const openEditSubcategory = (subId) => {
    const sub = subcategories.find((s) => s.id === subId);
    if (!sub) return;
    setIsSubEditMode(true);
    setCurrentSubId(subId);
    setSubName(sub.name || '');
    setSubFormError('');
    setSubFormSuccess('');
    setOpenSubcategoryDialog(true);
    handleSubCloseMenu();
  };

  const handleSubmitSubcategory = async () => {
    if (!subName.trim()) {
      setSubFormError('Subcategory name is required');
      return;
    }

    setSubSubmitting(true);
    setSubFormError('');
    setSubFormSuccess('');

    try {
      const formData = new FormData();
      formData.append('issetAddEditSubCat', '1');
      formData.append('name', subName.trim());
      formData.append('cat_id', selectedCategoryId);
      if (isSubEditMode && currentSubId) {
        formData.append('subcat_id', currentSubId);
      }

      const res = await fetch(`${config.reactApi_baseUrl}/react-api/menu/add-edit-subcategory.php`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const result = await res.json();

      if (result.status === true) {
        setSubFormSuccess('Subcategory saved successfully!');
        setTimeout(() => {
          fetchSubcategories();
          setOpenSubcategoryDialog(false);
        }, 1200);
      } else {
        setSubFormError(getFirstErrorMessage(result.errorMsg) || result.message || 'Failed to save');
      }
    } catch (err) {
      setSubFormError(err.message || 'Network/server error');
      console.error(err);
    } finally {
      setSubSubmitting(false);
    }
  };

  // ── Product Handlers ──
  const handleAddProduct = () => {
    setIsProductEditMode(false);
    setCurrentProdId('');
    setProdName('');
    setProdPrice('');
    setProdDescription('');
    setProdImageFile(null);
    setProdImagePreview('');
    setProdFormError('');
    setProdFormSuccess('');
    setOpenProductDialog(true);
  };

  const openEditProduct = (prodId) => {
    const prod = products.find((p) => p.id === prodId);
    if (!prod) return;

    setIsProductEditMode(true);
    setCurrentProdId(prodId);
    setProdName(prod.name || '');
    setProdPrice(prod.price || '');
    setProdDescription(prod.description || '');
    setProdImageFile(null);
    setProdImagePreview(prod.image ? `${config.reactApi_baseUrl}/media/products/md/${prod.image}` : '');
    setProdFormError('');
    setProdFormSuccess('');
    setOpenProductDialog(true);
    handleProdCloseMenu();
  };

  const handleProductImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setProdImageFile(file);
      setProdImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmitProduct = async () => {
    if (!prodName.trim()) {
      setProdFormError('Product name is required');
      return;
    }
    if (!prodPrice.trim()) {
      setProdFormError('Price is required');
      return;
    }
   
    setProdSubmitting(true);
    setProdFormError('');
    setProdFormSuccess('');

    try {
      const formData = new FormData();
      formData.append('issetAddEditPro', '1');
      formData.append('name', prodName.trim());
      formData.append('price', prodPrice.trim());
      formData.append('description', prodDescription.trim());
      formData.append('cat_id', selectedCategoryId);
      formData.append('subcat_id', selectedSubcategoryId);

      if (prodImageFile) {
        formData.append('image', prodImageFile);
      }

      if (isProductEditMode && currentProdId) {
        formData.append('pro_id', currentProdId);
      }

      const res = await fetch(`${config.reactApi_baseUrl}/react-api/menu/add-edit-product.php`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const result = await res.json();

      if (result.status === true) {
        setProdFormSuccess('Product saved successfully!');
        setTimeout(() => {
          fetchProducts();
          setOpenProductDialog(false);
        }, 1300);
      } else {
        setProdFormError(getFirstErrorMessage(result.errorMsg) || result.message || 'Failed to save product');
      }
    } catch (err) {
      setProdFormError(err.message || 'Network or server error');
      console.error('Product submit error:', err);
    } finally {
      setProdSubmitting(false);
    }
  };

  // ── Other Handlers ──
  const handleOpenMenu = (event, id) => {
    setAnchorEl(event.currentTarget);
    setSelectedId(id);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedId(null);
  };

  const handleSubOpenMenu = (event, subId) => {
    setSubAnchorEl(event.currentTarget);
    setSelectedSubId(subId);
  };

  const handleSubCloseMenu = () => {
    setSubAnchorEl(null);
    setSelectedSubId(null);
  };

  const handleProdOpenMenu = (event, prodId) => {
    setProdAnchorEl(event.currentTarget);
    setSelectedProdId(prodId);
  };

  const handleProdCloseMenu = () => {
    setProdAnchorEl(null);
    setSelectedProdId(null);
  };

  const handleOpenSubcategories = (catId, catName) => {
    setSelectedCategoryId(catId);
    setSelectedCategoryName(catName);
    setOpenSubDialog(true);
  };

  const handleOpenProducts = (subId, subName) => {
    setSelectedSubcategoryId(subId);
    setSelectedSubcategoryName(subName);
    setOpenProductsDialog(true);
  };

  const handleCloseSubDialog = () => {
    setOpenSubDialog(false);
    setSelectedCategoryId(null);
    setSelectedCategoryName('');
    setSubcategories([]);
    setSubError(null);
  };

  const handleCloseProductsDialog = () => {
    setOpenProductsDialog(false);
    setSelectedSubcategoryId(null);
    setSelectedSubcategoryName('');
    setProducts([]);
    setProductsError(null);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          MENU
        </Typography>
        <Button
          variant="contained"
          sx={{
            backgroundColor: '#ff2c6d',
            fontWeight: 700,
            '&:hover': { backgroundColor: '#e02660' },
          }}
          onClick={openAddCategory}
        >
          Add Category
        </Button>
      </Box>

      {/* Categories Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : (
        <TableContainer component={Paper} elevation={0}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Categories</TableCell>
                <TableCell sx={{ fontWeight: 700 }}></TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No categories found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                categories.map((cat) => (
                  <TableRow key={cat.id}>
                    <TableCell>{cat.name}</TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleOpenSubcategories(cat.id, cat.name)}
                      >
                        Subcategories
                      </Button>
                    </TableCell>
                    <TableCell sx={{ width: 0 }}>
                      <IconButton size="small" onClick={(e) => handleOpenMenu(e, cat.id)}>
                        <img
                          src={iconAngleDown}
                          width={16}
                          alt="action"
                          style={{
                            transition: '0.2s',
                            transform: selectedId === cat.id ? 'rotate(180deg)' : 'rotate(0deg)',
                          }}
                        />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Action Menus */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{ sx: { borderRadius: 2, width: 140 } }}
      >
        <MenuItem onClick={() => openEditCategory(selectedId)}>Edit</MenuItem>
        <MenuItem
          onClick={() => confirmDelete('category', selectedId)}
          sx={{ color: 'error.main' }}
        >
          Delete
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={subAnchorEl}
        open={Boolean(subAnchorEl)}
        onClose={handleSubCloseMenu}
        PaperProps={{ sx: { borderRadius: 2, width: 140 } }}
      >
        <MenuItem onClick={() => openEditSubcategory(selectedSubId)}>Edit</MenuItem>
        <MenuItem
          onClick={() => confirmDelete('subcategory', selectedSubId)}
          sx={{ color: 'error.main' }}
        >
          Delete
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={prodAnchorEl}
        open={Boolean(prodAnchorEl)}
        onClose={handleProdCloseMenu}
        PaperProps={{ sx: { borderRadius: 2, width: 140 } }}
      >
        <MenuItem onClick={() => openEditProduct(selectedProdId)}>Edit</MenuItem>
        <MenuItem
          onClick={() => confirmDelete('product', selectedProdId)}
          sx={{ color: 'error.main' }}
        >
          Delete
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => !deleteSubmitting && setOpenDeleteDialog(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          {deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}
          <DialogContentText>
            Are you sure you want to delete this {deleteType}?
            <br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            disabled={deleteSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirmed}
            disabled={deleteSubmitting}
          >
            {deleteSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={openCategoryDialog} onClose={() => setOpenCategoryDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditMode ? 'Edit Category' : 'Add New Category'}</DialogTitle>
        <DialogContent dividers>
          {catFormSuccess && <Alert severity="success" sx={{ mb: 2 }}>{catFormSuccess}</Alert>}
          {catFormError && <Alert severity="error" sx={{ mb: 2 }}>{catFormError}</Alert>}
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            fullWidth
            variant="outlined"
            value={catName}
            onChange={(e) => setCatName(e.target.value)}
            disabled={catSubmitting}
            error={!!catFormError && !catName.trim()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCategoryDialog(false)} disabled={catSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: '#ff2c6d', '&:hover': { backgroundColor: '#e02660' } }}
            onClick={handleSubmitCategory}
            disabled={catSubmitting || !catName.trim()}
          >
            {catSubmitting ? <CircularProgress size={24} /> : isEditMode ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Subcategory Dialog */}
      <Dialog open={openSubcategoryDialog} onClose={() => setOpenSubcategoryDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isSubEditMode ? 'Edit Subcategory' : 'Add New Subcategory'}</DialogTitle>
        <DialogContent dividers>
          {subFormSuccess && <Alert severity="success" sx={{ mb: 2 }}>{subFormSuccess}</Alert>}
          {subFormError && <Alert severity="error" sx={{ mb: 2 }}>{subFormError}</Alert>}
          <TextField
            autoFocus
            margin="dense"
            label="Subcategory Name"
            fullWidth
            variant="outlined"
            value={subName}
            onChange={(e) => setSubName(e.target.value)}
            disabled={subSubmitting}
            error={!!subFormError && !subName.trim()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSubcategoryDialog(false)} disabled={subSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: '#ff2c6d', '&:hover': { backgroundColor: '#e02660' } }}
            onClick={handleSubmitSubcategory}
            disabled={subSubmitting || !subName.trim()}
          >
            {subSubmitting ? <CircularProgress size={24} /> : isSubEditMode ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Product Dialog */}
      <Dialog open={openProductDialog} onClose={() => setOpenProductDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isProductEditMode ? 'Edit Product' : 'Add New Product'}</DialogTitle>
        <DialogContent dividers>
          {prodFormSuccess && <Alert severity="success" sx={{ mb: 2 }}>{prodFormSuccess}</Alert>}
          {prodFormError && <Alert severity="error" sx={{ mb: 2 }}>{prodFormError}</Alert>}

          <TextField
            margin="dense"
            label="Product Name"
            fullWidth
            variant="outlined"
            value={prodName}
            onChange={(e) => setProdName(e.target.value)}
            disabled={prodSubmitting}
            error={!!prodFormError && !prodName.trim()}
          />
          <TextField
            margin="dense"
            label="Price"
            fullWidth
            variant="outlined"
            value={prodPrice}
            onChange={(e) => setProdPrice(e.target.value)}
            disabled={prodSubmitting}
            error={!!prodFormError && !prodPrice.trim()}
          />

          
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={prodDescription}
            onChange={(e) => setProdDescription(e.target.value)}
            disabled={prodSubmitting}
          />

          <Box sx={{ mt: 3 }}>
            <input
              accept="image/*"
              id="product-image-upload"
              type="file"
              style={{ display: 'none' }}
              onChange={handleProductImageChange}
              disabled={prodSubmitting}
            />
            <label htmlFor="product-image-upload">
              <Button
                variant="outlined"
                component="span"
                disabled={prodSubmitting}
              >
                {prodImageFile ? 'Change Image' : 'Upload Image'}
              </Button>
            </label>

            {(prodImagePreview || prodImageFile) && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Avatar
                  variant="rounded"
                  src={prodImagePreview}
                  sx={{ width: 140, height: 140, mx: 'auto', border: '1px solid #ddd' }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Image Preview
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenProductDialog(false)} disabled={prodSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: '#ff2c6d', '&:hover': { backgroundColor: '#e02660' } }}
            onClick={handleSubmitProduct}
            disabled={prodSubmitting || !prodName.trim() || !prodPrice || Number(prodPrice) <= 0}
          >
            {prodSubmitting ? <CircularProgress size={24} /> : isProductEditMode ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Subcategories List Dialog */}
      <Dialog open={openSubDialog} onClose={handleCloseSubDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Subcategories of: {selectedCategoryName || 'Category'}</span>
            <Button
              variant="contained"
              sx={{ backgroundColor: '#ff2c6d', fontWeight: 700, '&:hover': { backgroundColor: '#e02660' } }}
              onClick={handleAddSubcategory}
            >
              Add Subcategory
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ minHeight: '300px' }}>
          {subLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
              <CircularProgress />
            </Box>
          ) : subError ? (
            <Alert severity="error">{subError}</Alert>
          ) : subcategories.length === 0 ? (
            <Typography align="center" color="text.secondary" sx={{ py: 8 }}>
              No subcategories found
            </Typography>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Subcategory</TableCell>
                    <TableCell></TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subcategories.map((sub) => (
                    <TableRow key={sub.id}>
                      <TableCell>{sub.name}</TableCell>
                      <TableCell sx={{ textAlign: 'right' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleOpenProducts(sub.id, sub.name)}
                        >
                          Products
                        </Button>
                      </TableCell>
                      <TableCell sx={{ width: 0 }}>
                        <IconButton size="small" onClick={(e) => handleSubOpenMenu(e, sub.id)}>
                          <img
                            src={iconAngleDown}
                            width={16}
                            alt="action"
                            style={{
                              transition: '0.2s',
                              transform: selectedSubId === sub.id ? 'rotate(180deg)' : 'rotate(0deg)',
                            }}
                          />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSubDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Products List Dialog */}
      <Dialog open={openProductsDialog} onClose={handleCloseProductsDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Products in: {selectedSubcategoryName || 'Subcategory'}</span>
            <Button
              variant="contained"
              sx={{ backgroundColor: '#ff2c6d', fontWeight: 700, '&:hover': { backgroundColor: '#e02660' } }}
              onClick={handleAddProduct}
            >
              Add Product
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ minHeight: '300px' }}>
          {productsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
              <CircularProgress />
            </Box>
          ) : productsError ? (
            <Alert severity="error">{productsError}</Alert>
          ) : products.length === 0 ? (
            <Typography align="center" color="text.secondary" sx={{ py: 8 }}>
              No products found
            </Typography>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }} width={80}>Image</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Price</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products.map((prod) => (
                    <TableRow key={prod.id}>
                      <TableCell>
                        <Avatar
                          variant="rounded"
                          src={`${config.reactApi_baseUrl}/media/products/md/${prod.image}`}
                          sx={{ width: 60, height: 60 }}
                        />
                      </TableCell>
                      <TableCell>{prod.name}</TableCell>
                      <TableCell>{prod.description}</TableCell>
                      <TableCell align="right">{prod.price}</TableCell>
                      <TableCell align="right" sx={{ width: 0 }}>
                        <IconButton size="small" onClick={(e) => handleProdOpenMenu(e, prod.id)}>
                          <img
                            src={iconAngleDown}
                            width={16}
                            alt="action"
                            style={{
                              transition: '0.2s',
                              transform: selectedProdId === prod.id ? 'rotate(180deg)' : 'rotate(0deg)',
                            }}
                          />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProductsDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}